// src/contexts/AdminDataContext.js
import React from 'react';
import pb from '../lib/pocketbase';

const AdminDataContext = React.createContext(null);

export function AdminDataProvider({ children }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const [settings, setSettings] = React.useState(null);
  const [customers, setCustomers] = React.useState([]);
  const [projects, setProjects] = React.useState([]); // vehicles
  const [services, setServices] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [quotes, setQuotes] = React.useState([]);
  const [invoices, setInvoices] = React.useState([]);
  const [meetings, setMeetings] = React.useState([]);

  const subsRef = React.useRef([]);

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsRes, customersRes, projectsRes, servicesRes, productsRes, quotesRes, invoicesRes, meetingsRes] = await Promise.all([
        (async () => { try { const r = await pb.collection('settings').getList(1, 1, { sort: '-created' }); return r?.items?.[0] || null; } catch { return null; } })(),
        (async () => { try { const r = await pb.collection('customers').getList(1, 200, { sort: '-created' }); return r?.items ?? []; } catch { return []; } })(),
        (async () => { try { const r = await pb.collection('projects').getList(1, 200, { sort: '-created' }); return r?.items ?? []; } catch { return []; } })(),
        (async () => { try { const r = await pb.collection('services_catalog').getList(1, 200, { sort: 'name' }); return r?.items ?? []; } catch { return []; } })(),
        (async () => { try { const r = await pb.collection('products').getList(1, 200, { sort: 'name' }); return r?.items ?? []; } catch { return []; } })(),
        (async () => { try { const r = await pb.collection('quotes').getList(1, 200, { sort: '-created', expand: 'customer_id' }); return r?.items ?? []; } catch { return []; } })(),
        (async () => { try { const r = await pb.collection('invoices').getList(1, 200, { sort: '-created', expand: 'customer_id' }); return r?.items ?? []; } catch { return []; } })(),
        (async () => { try { const r = await pb.collection('meetings').getList(1, 200, { sort: '-start' }); return r?.items ?? []; } catch { return []; } })(),
      ]);
      setSettings(settingsRes);
      setCustomers(customersRes);
      setProjects(projectsRes);
      setServices(servicesRes);
      setProducts(productsRes);
      setQuotes(quotesRes);
      setInvoices(invoicesRes);
      setMeetings(meetingsRes);
    } catch (e) {
      setError(e?.message || 'Erreur chargement global');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchAll();
    // PocketBase realtime subscriptions (lightweight, invalidate or patch lists)
    const sub = async (col, setter) => {
      try {
        const h = await pb.collection(col).subscribe('*', async () => {
          try {
            const opts = col === 'settings'
              ? { sort: '-created' }
              : (col === 'quotes' || col === 'invoices')
                ? { sort: '-created', expand: 'customer_id' }
                : { sort: '-created' };
            const list = await pb.collection(col).getList(1, 200, opts);
            if (col === 'settings') setter(list?.items?.[0] || null);
            else setter(list?.items ?? []);
          } catch { }
        });
        subsRef.current.push({ col, h });
      } catch { }
    };
    sub('customers', setCustomers);
    sub('projects', setProjects);
    sub('services_catalog', setServices);
    sub('products', setProducts);
    sub('quotes', setQuotes);
    sub('invoices', setInvoices);
    sub('settings', setSettings);
    sub('meetings', setMeetings);

    return () => {
      subsRef.current.forEach(({ col }) => {
        try {
          // Only unsubscribe if we still have a valid client connection
          if (pb.realtime.isConnected) {
            pb.collection(col).unsubscribe('*').catch(() => { });
          }
        } catch { }
      });
      subsRef.current = [];
    };
  }, [fetchAll]);

  const value = React.useMemo(() => ({
    loading,
    error,
    reload: fetchAll,
    settings,
    customers,
    projects,
    vehicles: projects, // alias
    services,
    products,
    quotes,
    invoices,
    meetings,
  }), [loading, error, fetchAll, settings, customers, projects, services, products, quotes, invoices, meetings]);

  return (
    <AdminDataContext.Provider value={value}>
      {children}
    </AdminDataContext.Provider>
  );
}

export function useAdminData() {
  return React.useContext(AdminDataContext);
}
